import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ImplementationRoadmap = ({ recommendations, allRecommendations, onRecommendationSelect }) => {
  const sortedRecommendations = recommendations?.sort((a, b) => a?.priority - b?.priority);

  const getPhaseColor = (index) => {
    const colors = ['bg-[#002B45]', 'bg-[#1FB5A5]', 'bg-purple-500', 'bg-[#002B45]'];
    return colors?.[index % colors?.length];
  };

  const calculateTotalTimeline = () => {
    return sortedRecommendations?.length * 4; // Assuming 4 months per recommendation
  };

  const calculateTotalInvestment = () => {
    return sortedRecommendations?.length * 50000; // Estimated investment per recommendation
  };

  if (recommendations?.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-elevation-1 p-12 text-center">
        <Icon name="Map" size={48} className="text-gray-300 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-[#002B45] mb-2">Build Your Implementation Roadmap</h3>
        <p className="text-gray-600 mb-6">
          Select recommendations from the grid view to create a custom implementation roadmap with automatic dependency mapping and timeline visualization.
        </p>
        <Button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          iconName="ArrowUp"
          iconPosition="left"
        >
          Go to Recommendations
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Roadmap Summary */}
      <div className="bg-white rounded-xl shadow-elevation-1 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-[#002B45] mb-2">Your Implementation Roadmap</h2>
            <p className="text-gray-600">Strategic execution plan with {sortedRecommendations?.length} prioritized actions</p>
          </div>
          <Icon name="Map" size={32} className="text-[#1FB5A5]" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-blue-50 to-teal-50 rounded-lg p-4 border border-blue-200">
            <p className="text-sm text-gray-600 mb-1">Total Timeline</p>
            <p className="text-3xl font-bold text-[#002B45]">{calculateTotalTimeline()} months</p>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-lg p-4 border border-green-200">
            <p className="text-sm text-gray-600 mb-1">Estimated Investment</p>
            <p className="text-3xl font-bold text-[#002B45]">${(calculateTotalInvestment() / 1000)?.toFixed(0)}K</p>
          </div>
          <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-lg p-4 border border-purple-200">
            <p className="text-sm text-gray-600 mb-1">Expected ROI</p>
            <p className="text-3xl font-bold text-[#1FB5A5]">300%+</p>
          </div>
        </div>
      </div>

      {/* Timeline Visualization */}
      <div className="bg-white rounded-xl shadow-elevation-1 p-6">
        <h3 className="text-lg font-bold text-[#002B45] mb-6 flex items-center gap-2">
          <Icon name="Calendar" size={20} className="text-[#1FB5A5]" />
          Phased Implementation Timeline
        </h3>

        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-8 top-0 bottom-0 w-1 bg-gradient-to-b from-[#1FB5A5] to-gray-200" />

          {/* Timeline Items */}
          <div className="space-y-8">
            {sortedRecommendations?.map((recommendation, index) => {
              const phaseColor = getPhaseColor(index);
              const startMonth = index * 4 + 1;
              const endMonth = startMonth + 3;

              return (
                <div key={recommendation?.id} className="relative pl-20">
                  {/* Phase Marker */}
                  <div className={`absolute left-4 w-8 h-8 rounded-full ${phaseColor} flex items-center justify-center text-white font-bold text-sm shadow-lg`}>
                    {index + 1}
                  </div>

                  {/* Phase Card */}
                  <div className="bg-gray-50 rounded-lg p-5 hover:shadow-md transition-shadow duration-300">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-semibold text-gray-500">PHASE {index + 1}</span>
                          <span className="text-xs text-gray-400">• Months {startMonth}-{endMonth}</span>
                        </div>
                        <h4 className="text-lg font-bold text-[#002B45] mb-2">{recommendation?.title}</h4>
                        <p className="text-sm text-gray-600">{recommendation?.description}</p>
                      </div>
                      <button
                        onClick={() => onRecommendationSelect(recommendation?.id)}
                        className="ml-4 text-red-500 hover:text-red-700 transition-colors"
                      >
                        <Icon name="X" size={20} />
                      </button>
                    </div>

                    {/* Phase Details */}
                    <div className="grid grid-cols-3 gap-3 mt-4">
                      <div className="bg-white rounded-lg p-3">
                        <p className="text-xs text-gray-500 mb-1">Duration</p>
                        <p className="text-sm font-semibold text-[#002B45]">4 months</p>
                      </div>
                      <div className="bg-white rounded-lg p-3">
                        <p className="text-xs text-gray-500 mb-1">Team Size</p>
                        <p className="text-sm font-semibold text-[#002B45]">2-3 people</p>
                      </div>
                      <div className="bg-white rounded-lg p-3">
                        <p className="text-xs text-gray-500 mb-1">Investment</p>
                        <p className="text-sm font-semibold text-[#002B45]">$50K</p>
                      </div>
                    </div>

                    {/* Key Milestones */}
                    {recommendation?.recommendedActions?.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <p className="text-xs font-semibold text-gray-700 mb-2">Key Milestones:</p>
                        <ul className="space-y-1">
                          {recommendation?.recommendedActions?.slice(0, 3)?.map((action, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-xs text-gray-600">
                              <Icon name="CheckCircle" size={12} className="text-[#1FB5A5] mt-0.5 flex-shrink-0" />
                              <span>{action?.action}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Add More Recommendations */}
      {allRecommendations?.length > recommendations?.length && (
        <div className="bg-white rounded-xl shadow-elevation-1 p-6">
          <div className="flex items-start gap-4">
            <Icon name="Plus" size={24} className="text-[#1FB5A5] flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-lg font-bold text-[#002B45] mb-2">Add More to Your Roadmap</h3>
              <p className="text-sm text-gray-600 mb-4">
                You have {allRecommendations?.length - recommendations?.length} additional recommendations available. Switch to grid view to add more actions to your implementation plan.
              </p>
              <Button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                variant="outline"
                iconName="Grid"
                iconPosition="left"
              >
                View All Recommendations
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImplementationRoadmap;